import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import WaveSurfer from 'wavesurfer.js';
import RegionsPlugin from 'wavesurfer.js/dist/plugins/regions.esm.js';
import TimelinePlugin from 'wavesurfer.js/dist/plugins/timeline.esm.js';
import SpectrogramPlugin from 'wavesurfer.js/dist/plugins/spectrogram.esm.js';
import { fetchApi } from '../utils/api';
import { AdminAuthError, adminFetch, isAdmin } from '../utils/admin';

const VIEWS = [
  { id: 'word', label: 'Words' },
  { id: 'syllable', label: 'Syllables' },
  { id: 'phoneme', label: 'Phonemes' },
];

// Edges closer than this are treated as touching, so linked-edge editing moves them together
const LINK_TOLERANCE = 0.002;
const MIN_LENGTH = 0.01;
const NUDGE = 0.01;
const FINE_NUDGE = 0.001;

const COLORS = ['rgba(111, 45, 168, 0.22)', 'rgba(224, 64, 251, 0.14)'];
const SELECTED_COLOR = 'rgba(255, 179, 0, 0.35)';

const roundTime = (t) => Math.round(t * 10000) / 10000;
const formatTime = (t) => t.toFixed(3);

let nextChunkId = 1;
const withIds = (chunks) => chunks.map((c) => ({ ...c, id: `chunk-${nextChunkId++}` }));
const sortChunks = (chunks) => [...chunks].sort((a, b) => a.start - b.start);

function makeLabelElement(text) {
  const el = document.createElement('div');
  el.textContent = text || '?';
  Object.assign(el.style, {
    position: 'absolute',
    bottom: '2px',
    left: '50%',
    transform: 'translateX(-50%)',
    fontWeight: 'bold',
    fontSize: '0.95em',
    color: text ? '#222' : '#b00',
    textShadow: '0 1px 4px #fff, 0 0 2px #fff',
    pointerEvents: 'none',
    whiteSpace: 'nowrap',
    userSelect: 'none',
  });
  return el;
}

function findLinkedNeighbors(chunks, chunk) {
  const prev = chunks.find((c) => c.id !== chunk.id && Math.abs(c.end - chunk.start) < LINK_TOLERANCE);
  const next = chunks.find((c) => c.id !== chunk.id && Math.abs(c.start - chunk.end) < LINK_TOLERANCE);
  return { prev, next };
}

function ChunkEditor() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [stratagem, setStratagem] = useState(null);
  const [error, setError] = useState(null);
  const [status, setStatus] = useState(null);
  const [view, setView] = useState('word');
  const [chunks, setChunks] = useState([]);
  const [edited, setEdited] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [linkEdges, setLinkEdges] = useState(true);
  const [zoom, setZoom] = useState(0);
  const [wsReady, setWsReady] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [loadingChunks, setLoadingChunks] = useState(false);
  const [saving, setSaving] = useState(false);

  const waveformRef = useRef(null);
  const spectrogramRef = useRef(null);
  const wavesurferRef = useRef(null);
  const regionsRef = useRef(null);
  const regionMapRef = useRef(new Map());
  const chunksRef = useRef([]);
  const linkEdgesRef = useRef(true);
  const selectedIdRef = useRef(null);
  const labelInputRef = useRef(null);

  chunksRef.current = chunks;
  linkEdgesRef.current = linkEdges;
  selectedIdRef.current = selectedId;

  const redirectToLogin = useCallback(() => {
    navigate(`/admin?next=${encodeURIComponent(`/admin/stratagem/${id}`)}`);
  }, [id, navigate]);

  const handleError = useCallback((err) => {
    if (err instanceof AdminAuthError) {
      redirectToLogin();
      return;
    }
    setError(err.message);
  }, [redirectToLogin]);

  const commitChunks = useCallback((next, { markDirty = true } = {}) => {
    const sorted = sortChunks(next);
    chunksRef.current = sorted;
    setChunks(sorted);
    if (markDirty) setDirty(true);
  }, []);

  const renderRegions = useCallback((list) => {
    const regions = regionsRef.current;
    if (!regions) return;
    regionMapRef.current = new Map();
    regions.clearRegions();
    list.forEach((chunk) => {
      // Register before adding so the region-created handler knows it isn't a user-drawn chunk
      regionMapRef.current.set(chunk.id, null);
      const region = regions.addRegion({
        id: chunk.id,
        start: chunk.start,
        end: chunk.end,
        content: makeLabelElement(chunk.label),
        drag: true,
        resize: true,
        minLength: MIN_LENGTH,
      });
      regionMapRef.current.set(chunk.id, region);
    });
  }, []);

  useEffect(() => {
    if (!isAdmin()) redirectToLogin();
  }, [redirectToLogin]);

  useEffect(() => {
    fetchApi(`/stratagems/${id}`)
      .then(setStratagem)
      .catch((err) => setError(err.message));
  }, [id]);

  // WaveSurfer setup, once the stratagem (and its audio file) is known
  useEffect(() => {
    const file = stratagem?.audio_clips?.[0]?.file;
    if (!file || !waveformRef.current) return undefined;

    const regions = RegionsPlugin.create();
    const ws = WaveSurfer.create({
      container: waveformRef.current,
      waveColor: '#4F4A85',
      progressColor: '#e040fb',
      cursorColor: '#383351',
      height: 128,
      plugins: [
        regions,
        TimelinePlugin.create(),
        SpectrogramPlugin.create({
          container: spectrogramRef.current,
          labels: true,
          height: 120,
        }),
      ],
    });
    wavesurferRef.current = ws;
    regionsRef.current = regions;

    ws.on('ready', () => setWsReady(true));
    ws.on('play', () => setIsPlaying(true));
    ws.on('pause', () => setIsPlaying(false));
    ws.on('finish', () => setIsPlaying(false));
    ws.on('error', (err) => setError(`Audio failed to load: ${err?.message || err}`));

    regions.enableDragSelection({ color: SELECTED_COLOR, drag: true, resize: true, minLength: MIN_LENGTH });

    regions.on('region-created', (region) => {
      if (regionMapRef.current.has(region.id)) return;
      const chunk = { id: `chunk-${nextChunkId++}`, start: roundTime(region.start), end: roundTime(region.end), label: '' };
      region.setOptions({ id: chunk.id, content: makeLabelElement('') });
      regionMapRef.current.set(chunk.id, region);
      commitChunks([...chunksRef.current, chunk]);
      setSelectedId(chunk.id);
      setTimeout(() => labelInputRef.current?.focus(), 0);
    });

    // Live linked-edge following while dragging or resizing
    regions.on('region-update', (region, side) => {
      if (!linkEdgesRef.current) return;
      const committed = chunksRef.current.find((c) => c.id === region.id);
      if (!committed) return;
      const { prev, next } = findLinkedNeighbors(chunksRef.current, committed);
      if (prev && side !== 'end') {
        regionMapRef.current.get(prev.id)?.setOptions({ end: Math.max(prev.start + MIN_LENGTH, region.start) });
      }
      if (next && side !== 'start') {
        regionMapRef.current.get(next.id)?.setOptions({ start: Math.min(next.end - MIN_LENGTH, region.end) });
      }
    });

    regions.on('region-updated', () => {
      const next = chunksRef.current.map((chunk) => {
        const region = regionMapRef.current.get(chunk.id);
        return region ? { ...chunk, start: roundTime(region.start), end: roundTime(region.end) } : chunk;
      });
      commitChunks(next);
    });

    regions.on('region-clicked', (region) => {
      setSelectedId(region.id);
    });

    ws.load(`/audio/stratagems/${stratagem.id}/${file}`);

    return () => {
      setWsReady(false);
      regionMapRef.current = new Map();
      regionsRef.current = null;
      wavesurferRef.current = null;
      ws.destroy();
    };
  }, [stratagem, commitChunks]);

  // Load chunks for the current view
  useEffect(() => {
    if (!wsReady) return undefined;
    let cancelled = false;
    setLoadingChunks(true);
    setError(null);
    fetchApi(`/stratagems/${id}/chunks?view=${view}`)
      .then((data) => {
        if (cancelled) return;
        const list = sortChunks(withIds(data.chunks));
        commitChunks(list, { markDirty: false });
        setEdited(data.edited);
        setDirty(false);
        setSelectedId(null);
        renderRegions(list);
      })
      .catch((err) => !cancelled && handleError(err))
      .finally(() => !cancelled && setLoadingChunks(false));
    return () => {
      cancelled = true;
    };
  }, [id, view, wsReady, commitChunks, renderRegions, handleError]);

  // Region colors reflect alternation and selection
  useEffect(() => {
    chunks.forEach((chunk, idx) => {
      const region = regionMapRef.current.get(chunk.id);
      region?.setOptions({ color: chunk.id === selectedId ? SELECTED_COLOR : COLORS[idx % 2] });
    });
  }, [chunks, selectedId]);

  useEffect(() => {
    if (wsReady) wavesurferRef.current?.zoom(zoom);
  }, [zoom, wsReady]);

  useEffect(() => {
    if (!dirty) return undefined;
    const warn = (e) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty]);

  const updateChunk = useCallback((chunkId, changes) => {
    const list = chunksRef.current;
    const chunk = list.find((c) => c.id === chunkId);
    if (!chunk) return;
    const duration = wavesurferRef.current?.getDuration() || Infinity;

    let start = changes.start ?? chunk.start;
    let end = changes.end ?? chunk.end;
    if (changes.start != null && changes.end != null) {
      // Moving the whole chunk: keep its length while clamping to the clip
      const length = chunk.end - chunk.start;
      start = Math.min(Math.max(0, start), duration - length);
      end = start + length;
    } else {
      start = Math.min(Math.max(0, start), end - MIN_LENGTH);
      end = Math.max(Math.min(duration, end), start + MIN_LENGTH);
    }
    start = roundTime(start);
    end = roundTime(end);

    const updates = new Map([[chunkId, { ...chunk, ...changes, start, end }]]);
    if (linkEdgesRef.current) {
      const { prev, next } = findLinkedNeighbors(list, chunk);
      if (prev && start !== chunk.start) {
        updates.set(prev.id, { ...prev, end: roundTime(Math.max(prev.start + MIN_LENGTH, start)) });
      }
      if (next && end !== chunk.end) {
        updates.set(next.id, { ...next, start: roundTime(Math.min(next.end - MIN_LENGTH, end)) });
      }
    }

    updates.forEach((updated, updatedId) => {
      const region = regionMapRef.current.get(updatedId);
      if (!region) return;
      region.setOptions({ start: updated.start, end: updated.end });
      if (changes.label !== undefined && updatedId === chunkId) {
        region.setContent(makeLabelElement(updated.label));
      }
    });

    commitChunks(list.map((c) => updates.get(c.id) || c));
  }, [commitChunks]);

  const deleteChunk = useCallback((chunkId) => {
    regionMapRef.current.get(chunkId)?.remove();
    regionMapRef.current.delete(chunkId);
    commitChunks(chunksRef.current.filter((c) => c.id !== chunkId));
    setSelectedId(null);
  }, [commitChunks]);

  const nudgeChunk = useCallback((chunkId, delta) => {
    const chunk = chunksRef.current.find((c) => c.id === chunkId);
    if (chunk) updateChunk(chunkId, { start: chunk.start + delta, end: chunk.end + delta });
  }, [updateChunk]);

  const playChunk = useCallback((chunkId) => {
    regionMapRef.current.get(chunkId)?.play(true);
  }, []);

  // Keyboard shortcuts (ignored while typing in a field)
  useEffect(() => {
    const onKeyDown = (e) => {
      if (['INPUT', 'TEXTAREA', 'SELECT'].includes(e.target.tagName)) return;
      const selected = selectedIdRef.current;
      if (e.code === 'Space') {
        e.preventDefault();
        wavesurferRef.current?.playPause();
      } else if ((e.key === 'Delete' || e.key === 'Backspace') && selected) {
        e.preventDefault();
        deleteChunk(selected);
      } else if ((e.key === 'ArrowLeft' || e.key === 'ArrowRight') && selected) {
        e.preventDefault();
        const step = e.shiftKey ? FINE_NUDGE : NUDGE;
        nudgeChunk(selected, e.key === 'ArrowLeft' ? -step : step);
      } else if (e.key === 'Escape') {
        setSelectedId(null);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [deleteChunk, nudgeChunk]);

  const handleViewChange = (nextView) => {
    if (nextView === view) return;
    if (dirty && !window.confirm('You have unsaved changes in this view. Discard them?')) return;
    setView(nextView);
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      await adminFetch(`/stratagems/${id}/chunks/${view}`, {
        method: 'PUT',
        body: JSON.stringify({
          chunks: chunksRef.current.map(({ start, end, label }) => ({ start, end, label })),
        }),
      });
      setEdited(true);
      setDirty(false);
      setStatus('Saved.');
      setTimeout(() => setStatus(null), 2500);
    } catch (err) {
      handleError(err);
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    const message = edited
      ? `Discard all saved edits for the ${view} view and go back to the automatic chunks?`
      : 'Discard your unsaved changes and reload the automatic chunks?';
    if (!window.confirm(message)) return;
    setSaving(true);
    setError(null);
    try {
      const data = edited
        ? await adminFetch(`/stratagems/${id}/chunks/${view}`, { method: 'DELETE' })
        : await fetchApi(`/stratagems/${id}/chunks?view=${view}`);
      const list = sortChunks(withIds(data.chunks));
      commitChunks(list, { markDirty: false });
      setEdited(false);
      setDirty(false);
      setSelectedId(null);
      renderRegions(list);
    } catch (err) {
      handleError(err);
    } finally {
      setSaving(false);
    }
  };

  const handleBack = () => {
    if (dirty && !window.confirm('You have unsaved changes. Leave anyway?')) return;
    navigate(`/stratagem/${id}`);
  };

  const selected = chunks.find((c) => c.id === selectedId);

  const commitTimeInput = (field) => (e) => {
    const value = parseFloat(e.target.value);
    if (!selected || Number.isNaN(value)) {
      e.target.value = selected ? formatTime(selected[field]) : '';
      return;
    }
    updateChunk(selected.id, { [field]: value });
  };

  const timeInputKeyDown = (e) => {
    if (e.key === 'Enter') e.target.blur();
  };

  if (!stratagem) {
    return (
      <div className="section">
        <p className={`subtitle ${error ? 'has-text-danger' : ''}`}>{error ? `Error: ${error}` : 'Loading stratagem...'}</p>
      </div>
    );
  }

  if (!stratagem.audio_clips?.length) {
    return <div className="section"><p className="subtitle">No audio clips available for this stratagem.</p></div>;
  }

  return (
    <div className="section">
      <button className="button is-link mb-4" onClick={handleBack}>Back to stratagem</button>
      <h2 className="title is-2 mb-2">Edit chunks: {stratagem.name}</h2>
      <p className="mb-4">
        Drag a chunk to slide it, or drag its edges to resize it. Drag across an empty part of the waveform to add a new chunk.
        Click a chunk to select it. Shortcuts: Space plays or pauses, arrow keys nudge the selected chunk by 10 ms (1 ms with Shift),
        Delete removes it.
      </p>

      <div className="box">
        <div className="level mb-3">
          <div className="level-left">
            <div className="buttons has-addons mb-0 mr-3">
              {VIEWS.map((v) => (
                <button
                  key={v.id}
                  className={`button ${view === v.id ? 'is-primary' : ''}`}
                  onClick={() => handleViewChange(v.id)}
                >
                  {v.label}
                </button>
              ))}
            </div>
            <span className={`tag ${edited ? 'is-warning' : 'is-light'} mr-2`}>{edited ? 'Edited' : 'Automatic'}</span>
            {dirty && <span className="tag is-danger is-light">Unsaved changes</span>}
          </div>
          <div className="level-right">
            <label className="checkbox mr-4">
              <input type="checkbox" checked={linkEdges} onChange={(e) => setLinkEdges(e.target.checked)} />
              {' '}Linked edges
            </label>
            <label className="mr-2" htmlFor="chunk-zoom">Zoom</label>
            <input
              id="chunk-zoom"
              type="range"
              min="0"
              max="1000"
              step="10"
              value={zoom}
              onChange={(e) => setZoom(Number(e.target.value))}
              disabled={!wsReady}
            />
          </div>
        </div>

        <div ref={waveformRef} style={{ minHeight: '128px', border: '1px solid #383351', borderRadius: '4px', marginBottom: '0.5rem' }} />
        <div ref={spectrogramRef} style={{ minHeight: '120px', border: '1px solid #383351', borderRadius: '4px', marginBottom: '1rem' }} />

        <div className="buttons">
          <button className="button is-primary" onClick={() => wavesurferRef.current?.playPause()} disabled={!wsReady}>
            {isPlaying ? 'Pause' : 'Play'}
          </button>
          <button className={`button is-success ${saving ? 'is-loading' : ''}`} onClick={handleSave} disabled={!dirty || saving}>
            Save {view} chunks
          </button>
          <button className="button is-danger is-outlined" onClick={handleReset} disabled={saving || (!edited && !dirty)}>
            Reset to automatic
          </button>
          {loadingChunks && <span className="ml-2">Loading chunks...</span>}
          {status && <span className="ml-2 has-text-success">{status}</span>}
        </div>
        {error && <p className="has-text-danger">Error: {error}</p>}
      </div>

      <div className="columns">
        <div className="column is-5">
          <div className="box">
            <h3 className="title is-5">Selected chunk</h3>
            {selected ? (
              <>
                <div className="field">
                  <label className="label" htmlFor="chunk-label">Label</label>
                  <input
                    id="chunk-label"
                    ref={labelInputRef}
                    className="input"
                    value={selected.label}
                    maxLength={50}
                    onChange={(e) => updateChunk(selected.id, { label: e.target.value })}
                  />
                </div>
                {['start', 'end'].map((field) => (
                  <div className="field" key={field}>
                    <label className="label" htmlFor={`chunk-${field}`}>{field === 'start' ? 'Start' : 'End'} (seconds)</label>
                    <div className="field has-addons">
                      <div className="control">
                        <button className="button" onClick={() => updateChunk(selected.id, { [field]: selected[field] - NUDGE })}>-10 ms</button>
                      </div>
                      <div className="control">
                        <input
                          id={`chunk-${field}`}
                          key={`${selected.id}-${field}-${selected[field]}`}
                          className="input"
                          type="number"
                          step="0.001"
                          min="0"
                          defaultValue={formatTime(selected[field])}
                          onBlur={commitTimeInput(field)}
                          onKeyDown={timeInputKeyDown}
                          style={{ width: '8em' }}
                        />
                      </div>
                      <div className="control">
                        <button className="button" onClick={() => updateChunk(selected.id, { [field]: selected[field] + NUDGE })}>+10 ms</button>
                      </div>
                    </div>
                  </div>
                ))}
                <div className="buttons">
                  <button className="button is-info" onClick={() => playChunk(selected.id)}>Play chunk</button>
                  <button className="button is-danger" onClick={() => deleteChunk(selected.id)}>Delete</button>
                </div>
              </>
            ) : (
              <p>Click a chunk on the waveform or in the list to edit it.</p>
            )}
          </div>
        </div>

        <div className="column">
          <div className="box">
            <h3 className="title is-5">All {view} chunks ({chunks.length})</h3>
            <div style={{ maxHeight: '320px', overflowY: 'auto' }}>
              <table className="table is-narrow is-hoverable is-fullwidth">
                <thead>
                  <tr><th>Label</th><th>Start</th><th>End</th><th>Length</th></tr>
                </thead>
                <tbody>
                  {chunks.map((chunk) => (
                    <tr
                      key={chunk.id}
                      className={chunk.id === selectedId ? 'is-selected' : ''}
                      onClick={() => setSelectedId(chunk.id)}
                      style={{ cursor: 'pointer' }}
                    >
                      <td>{chunk.label || <em className="has-text-danger">unlabeled</em>}</td>
                      <td>{formatTime(chunk.start)}</td>
                      <td>{formatTime(chunk.end)}</td>
                      <td>{formatTime(chunk.end - chunk.start)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ChunkEditor;
